import { useState, useEffect } from "react";
import {
  ColaboradorService,
  GrupoPessoasService,
  MarcasService,
  MeioPagamentoService,
  ModeloGradeService,
  TipoContribuinteClienteService,
} from "../../../../services";
import {
  Grid,
  TextField,
  FormControl,
  Autocomplete,
  CurrencyInput,
  Select,
  MenuItem,
  Tooltip,
  Button,
} from "../../../../components";
import { value } from "../../../../helpers";
import { cacheApi, responseErros, unformatPrice } from "../../../../utils";
import { filtrosUrl } from "../../../../helpers";
import DependentesDialog from "./dependentes-dialog.component";

export default function FormCliente({
  clienteFormRef,
  cliente,
  setCliente,
  habilitado,
  inputErros,
  cacheDto,
  setCacheDto,
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [listaGrupoPessoa, setListaGrupoPessoa] = useState([]);
  const [tiposContribuinte, setTiposContribuinte] = useState([]);
  const [listaVendedores, setListaVendedores] = useState([]);
  const [listaMeiosPagamento, setMeiosPagamento] = useState([]);
  const [listaMarcas, setListaMarcas] = useState([]);
  const [listaGrades, setListaGrades] = useState([]);
  const grupoPessoasService = new GrupoPessoasService();
  const tipoContribuinteClienteService = new TipoContribuinteClienteService();
  const colaboradorService = new ColaboradorService();
  const meioPagamentoService = new MeioPagamentoService();
  const marcasService = new MarcasService();
  const modeloGradeService = new ModeloGradeService();

  useEffect(() => {
    buscarTipoContribuinte();
    buscaGruposPessoas();
    buscarVendedores();
    buscarMeiosPagamento();
    buscarMarcas();
    buscarGrades();
  }, []);

  const buscaGruposPessoas = async () => {
    const filtros = {
      ativado: true,
      tipoPessoa: 1,
      restritiva: true,
    };
    const label = filtrosUrl.getUrl(filtros, grupoPessoasService.urlBase);
    const cacheBody = {
      apiFunction: grupoPessoasService.getAllFiltroAvancado(filtros),
      dto: { cacheDto, setCacheDto },
    };
    const res = await cacheApi(label, cacheBody);
    if (!res.isAxiosError) {
      setListaGrupoPessoa(res.data.rows);
    } else {
      responseErros(res);
    }
  };

  const buscarTipoContribuinte = async () => {
    const cacheBody = {
      timeMinutes: 60,
      apiFunction: tipoContribuinteClienteService.getAll(),
      dto: { cacheDto, setCacheDto },
    };
    const res = await cacheApi(
      tipoContribuinteClienteService.urlBase,
      cacheBody
    );
    if (!res.isAxiosError) {
      setTiposContribuinte(res.data);
    } else {
      responseErros(res);
    }
  };

  const buscarVendedores = async (filtros) => {
    const filtro = {
      ...filtros,
      nonPaginated: true,
      vendedor: true,
    };
    const result = await colaboradorService.getAll(filtro);
    if (!result.isAxiosError) {
      const pessoas = result.data.map((item) => item.pessoa);
      setListaVendedores(pessoas);
    } else {
      responseErros(result);
    }
  };

  const buscarMeiosPagamento = async (filtros) => {
    const filtro = {
      ...filtros,
      nonPaginated: true,
      orderBy: "descricao",
      order: "asc",
    };
    const result = await meioPagamentoService.getAll(filtro);
    if (!result.isAxiosError) {
      setMeiosPagamento(result.data);
    } else {
      responseErros(result);
    }
  };

  const buscarMarcas = async function (filter) {
    const result = await marcasService.getAll(filter);
    if (!result.isAxiosError) {
      setListaMarcas(result.data.rows);
    } else {
      responseErros(result);
    }
  };

  const buscarGrades = async function () {
    const result = await modeloGradeService.getAllVariacoes();
    if (!result.isAxiosError) {
      setListaGrades(result.data);
    } else {
      responseErros(result);
    }
  };

  const handleChangeAutocomplete = (name, value) => {
    const clienteTemp = {
      ...cliente,
      [name]: value ? value.id : null,
    };
    if (
      name === "grupoPessoaId" &&
      value?.tabelaPrecoId &&
      !cliente?.tabelaPrecoPadraoId
    )
      clienteTemp.tabelaPrecoPadraoId = value.tabelaPrecoId;
    setCliente(clienteTemp);
  };

  const handleChangeInput = (event) => {
    setCliente({
      ...cliente,
      [event.target.name]: event.target.value ? event.target.value : null,
    });
  };

  const handleInputBlur = (e) => {
    setCliente((prevState) => ({
      ...prevState,
      [e.target.name]: unformatPrice(e.target.value),
    }));
  };

  const getMultipleAutocompleteValues = (valores, lista) => {
    if (valores) {
      return valores.map((valor) => value.autoComplete(lista, valor.id));
    }
    return [];
  };

  return (
    <>
      <form ref={clienteFormRef}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Select
              label="Consumidor Final"
              id="indicadorConsumidorFinal"
              name="indicadorConsumidorFinal"
              margin="normal"
              disabled={!habilitado}
              variant={habilitado ? "outlined" : "filled"}
              value={cliente?.indicadorConsumidorFinal || false}
              required
              onChange={(event) => {
                setCliente({
                  ...cliente,
                  [event.target.name]: event.target.value,
                });
              }}
              error={inputErros.get("indicadorConsumidorFinal")}
            >
              <MenuItem value={true}>Sim</MenuItem>
              <MenuItem value={false}>Não</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="tipoContribuinteId"
                name="tipoContribuinteId"
                options={tiposContribuinte}
                noOptionsText="Sem opções"
                autoHighlight
                disabled={!habilitado}
                getOptionLabel={(option) =>
                  option.descricao ? option.descricao : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo Contribuinte"
                    required
                    variant={habilitado ? "outlined" : "filled"}
                    error={inputErros.get("tipoContribuinteId")}
                  />
                )}
                onChange={(e, value) =>
                  handleChangeAutocomplete("tipoContribuinteId", value)
                }
                value={value.autoComplete(
                  tiposContribuinte,
                  cliente.tipoContribuinteId
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="grupoPessoaId"
                name="grupoPessoaId"
                disabled={!habilitado}
                options={listaGrupoPessoa}
                noOptionsText="Sem opções"
                autoHighlight
                getOptionLabel={(option) => (option.nome ? option.nome : "")}
                filterSelectedOptions
                onChange={(_, value) => {
                  handleChangeAutocomplete("grupoPessoaId", value);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant={habilitado ? "outlined" : "filled"}
                    label="Grupos de Pessoas"
                  />
                )}
                value={value.autoComplete(
                  listaGrupoPessoa,
                  cliente.grupoPessoaId
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                id="sugestaoVendedorId"
                name="sugestaoVendedorId"
                options={listaVendedores}
                noOptionsText="Sem opções"
                autoHighlight
                disabled={!habilitado}
                getOptionLabel={(option) =>
                  option ? option.nomeRazaoSocial : ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sugestão de Vendedor"
                    variant={habilitado ? "outlined" : "filled"}
                    error={inputErros.get("sugestaoVendedorId")}
                  />
                )}
                onChange={(e, value) =>
                  handleChangeAutocomplete("sugestaoVendedorId", value)
                }
                value={value.autoComplete(
                  listaVendedores,
                  cliente.sugestaoVendedorId
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <CurrencyInput
              label="Limite de Crédito"
              name="limiteCompra"
              disabled={!habilitado}
              variant={habilitado ? "outlined" : "filled"}
              onBlur={handleInputBlur}
              value={cliente.limiteCompra ?? ""}
            />
          </Grid>
          <Grid item xs={3}>
            <Autocomplete
              multiple
              id="marcas"
              name="marcas"
              options={listaMarcas}
              noOptionsText="Sem opções"
              autoHighlight
              disabled={habilitado ? false : true}
              getOptionLabel={(option) => (option ? option.nome : "")}
              onChange={(_, newValue) => {
                setCliente((prevState) => ({
                  ...prevState,
                  marcas: newValue,
                }));
              }}
              value={getMultipleAutocompleteValues(
                cliente?.marcas,
                listaMarcas
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Marcas"
                  margin="normal"
                  variant={habilitado ? "outlined" : "filled"}
                  error={inputErros.get("marcas")}
                />
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Autocomplete
              multiple
              id="grades"
              name="grades"
              options={listaGrades}
              noOptionsText="Sem opções"
              autoHighlight
              disabled={habilitado ? false : true}
              getOptionLabel={(option) => (option ? option.valor : "")}
              onChange={(_, newValue) => {
                setCliente((prevState) => ({
                  ...prevState,
                  grades: newValue,
                }));
              }}
              value={getMultipleAutocompleteValues(
                cliente?.grades,
                listaGrades
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modelos de Grade"
                  margin="normal"
                  variant={habilitado ? "outlined" : "filled"}
                  error={inputErros.get("grades")}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <Tooltip title="Adicione dependentes desse cliente" placement="top">
              <Button
                size="large"
                variant="contained"
                color="primary"
                fullWidth
                disabled={!habilitado}
                sx={{ height: "52px", marginTop: "15px" }}
                onClick={() => setOpenDialog(true)}
              >
                Dependentes
              </Button>
            </Tooltip>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Mensagem para mostrar na venda"
              name="mensagemVenda"
              onChange={handleChangeInput}
              value={cliente.mensagemVenda ?? ""}
              disabled={!habilitado}
              variant={habilitado ? "outlined" : "filled"}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              id="meiosPagamento"
              name="meiosPagamento"
              options={listaMeiosPagamento}
              noOptionsText="Sem opções"
              autoHighlight
              disabled={habilitado ? false : true}
              getOptionLabel={(option) => (option ? option.descricao : "")}
              onChange={(_, newValue) => {
                setCliente((prevState) => ({
                  ...prevState,
                  meiosPagamento: newValue,
                }));
              }}
              value={getMultipleAutocompleteValues(
                cliente?.meiosPagamento,
                listaMeiosPagamento
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Meios de Pagamento"
                  variant={habilitado ? "outlined" : "filled"}
                  error={inputErros.get("meiosPagamento")}
                />
              )}
            />
          </Grid>
        </Grid>
      </form>
      <DependentesDialog
        open={openDialog}
        setOpen={setOpenDialog}
        cliente={cliente}
        setCliente={setCliente}
        modelosGrade={listaGrades}
      />
    </>
  );
}
